import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useGetApiCustomers,
  usePostApiContacts,
  usePutApiContactsId,
  getApiContacts,
  type GetApiContacts200,
  type GetApiCustomers200,
} from "@charphq/api-client";
import { useTableQuery } from "@charphq/ui/hooks/useTableQuery";
import { useRouterURLStateSync } from "@charphq/ui/hooks/use-router-url-state-sync";
// import type { GetApiCustomers200DataItem } from "@charphq/api-client";
import { ContactsPage } from "@charphq/ui/components/layout/pages/ContactsPage";
import type {
  Contact,
  Customer,
  URLStateSync,
} from "@charphq/ui/components/layout/pages/ContactsPage";

export const Route = createFileRoute("/customers/contacts")({
  component: ContactsPageComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Number(search.page) || 1,
    pageSize: Number(search.pageSize) || 50,
    sortBy: (search.sortBy as string) || undefined,
    sortDirection: (search.sortDirection as "asc" | "desc") || undefined,
    globalFilter: (search.globalFilter as string) || undefined,
    columnFilters: (search.columnFilters as Record<string, unknown>) || undefined,
  }),
});

// Use the generated types with required fields
// type Customer = Required<GetApiCustomers200DataItem>;

function ContactsPageComponent() {
  const navigate = useNavigate();
  const search = Route.useSearch();

  // Fetch customers for the contact form
  const { data: customersData } = useGetApiCustomers(
    {},
    {
      query: {
        enabled: true,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
      },
    },
  );

  // Mutation hooks for create and update
  const createContactMutation = usePostApiContacts();
  const updateContactMutation = usePutApiContactsId();

  const customers = ((customersData as GetApiCustomers200 | undefined)?.data || []) as Customer[];

  const tableQuery = useTableQuery<Contact>(
    ["contacts"],
    async (params) => {
      const response: GetApiContacts200 = await getApiContacts({
        q: params.globalFilter,
        page: params.page,
        limit: params.pageSize,
        sortBy: params.sortBy,
        sortOrder: params.sortDirection,
      });

      return {
        data: response.data as Contact[],
        pagination: response.pagination,
      };
    },
    {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  const { urlStateSync }: { urlStateSync: URLStateSync } = useRouterURLStateSync({
    searchParams: search,
    navigate,
    targetPath: "/customers/contacts",
    // Must match ContactsPage's initialPageSize (50) — a mismatch causes a
    // replace-navigation on every mount.
    defaultPageSize: 50,
    replace: true,
  });

  return (
    <ContactsPage
      tableQuery={tableQuery}
      urlStateSync={urlStateSync}
      customers={customers}
      onCreateContact={async (data) => {
        await createContactMutation.mutateAsync({
          data: {
            customerId: data.customerId,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email || undefined,
            phone: data.phone || undefined,
            position: data.position || undefined,
          },
        });
        // Data will be refreshed automatically by the dataFetcher
      }}
      onUpdateContact={async (id, data) => {
        await updateContactMutation.mutateAsync({
          id: id,
          data: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email || undefined,
            phone: data.phone || undefined,
            position: data.position || undefined,
            isActive: data.isActive,
          },
        });
        // Data will be refreshed automatically by the dataFetcher
      }}
      onDeleteContact={async (id) => {
        // Add delete mutation here when available
        // TODO: Add delete mutation when available
        void id;
        // Data will be refreshed automatically by the dataFetcher
      }}
      onViewContact={(id) => navigate({ to: `/customers/contacts/${id}` })}
      isCreating={createContactMutation.isPending}
      isUpdating={updateContactMutation.isPending}
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Customers", href: "/customers" },
        { label: "Contacts" },
      ]}
    />
  );
}
